import type { TranslateFn } from '@newo/localization';
import z from 'zod';
import { AgentIntroSourceType } from './TrainingSourceSelector.type';

const validateWebsiteUrl = (url: string): boolean => {
	if (/\s/.test(url)) {
		return false;
	}
	try {
		const urlToValidate = url.startsWith('http') ? url : `https://${url}`;
		const parsed = new URL(urlToValidate);
		const tld = parsed.hostname.split('.').at(-1) ?? '';
		return (
			['http:', 'https:'].includes(parsed.protocol) &&
			parsed.hostname.includes('.') &&
			tld.length >= 2
		);
	} catch {
		return false;
	}
};

const getWebsiteUrlError = (
	url: string,
	translate: TranslateFn
): string | null => {
	if (!url) {
		return translate(
			'creator',
			'trainingSourceSelection.validation.websiteRequired'
		);
	}
	if (/\s/.test(url)) {
		return translate(
			'creator',
			'trainingSourceSelection.validation.websiteInvalidSpaces'
		);
	}
	if (!validateWebsiteUrl(url)) {
		return translate(
			'creator',
			'trainingSourceSelection.validation.websiteInvalid'
		);
	}
	return null;
};

const validateGoogleMapsUrl = (url: string): boolean => {
	if (/\s/.test(url)) {
		return false;
	}
	try {
		const urlToValidate = url.startsWith('http') ? url : `https://${url}`;
		const parsed = new URL(urlToValidate);
		if (!['http:', 'https:'].includes(parsed.protocol)) {
			return false;
		}
		const { hostname, pathname } = parsed;

		if (hostname === 'maps.app.goo.gl') {
			return true;
		}
		if (/^maps\.google\.[a-z.]+$/i.test(hostname)) {
			return true;
		}
		if (
			/^(www\.)?google\.[a-z.]+$/i.test(hostname) &&
			pathname.startsWith('/maps')
		) {
			return true;
		}
		if (hostname === 'goo.gl' && pathname.startsWith('/maps')) {
			return true;
		}
		return false;
	} catch {
		return false;
	}
};

const getGoogleMapUrlError = (
	url: string,
	translate: TranslateFn
): string | null => {
	if (!url) {
		return translate(
			'creator',
			'trainingSourceSelection.validation.googleMapRequired'
		);
	}
	if (!validateGoogleMapsUrl(url)) {
		return translate(
			'creator',
			'trainingSourceSelection.validation.googleMapInvalid'
		);
	}
	return null;
};

export const createTrainingSourceSelectorSchema = (translate: TranslateFn) =>
	z
		.object({
			sourceType: z.nativeEnum(AgentIntroSourceType),
			websiteUrl: z.string().optional(),
			googleMapUrl: z.string().optional()
		})
		.superRefine((data, ctx) => {
			if (data.sourceType === AgentIntroSourceType.Website) {
				const error = getWebsiteUrlError(data.websiteUrl ?? '', translate);
				if (error) {
					ctx.addIssue({
						path: ['websiteUrl'],
						message: error,
						code: z.ZodIssueCode.custom
					});
				}
			}

			if (data.sourceType === AgentIntroSourceType.GoogleMap) {
				const error = getGoogleMapUrlError(data.googleMapUrl ?? '', translate);
				if (error) {
					ctx.addIssue({
						path: ['googleMapUrl'],
						message: error,
						code: z.ZodIssueCode.custom
					});
				}
			}
		});
